import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Marca, DemoBranding } from "@/components/MarcaDefinicoes";
import { AgenciaDefinicoes } from "@/components/AgenciaDefinicoes";
import { MensagensDefinicoes } from "@/components/MensagensDefinicoes";
import { ReviewBy, TreatDefault, CmiCertToggle, KycToggle, AssinajaToggle } from "@/components/ReviewBy";
import { AutoAccept } from "@/components/AutoAccept";
import { GestaoDefinicoes } from "@/components/GestaoDefinicoes";
import { CmiDefaults } from "@/components/CmiDefaults";
import { SetupCard } from "@/components/setup/SetupWizard";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Copy, UserPlus, MessageCircle, Sparkles, CheckCircle2 } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";

export function EntradaAutomatica() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/agency/domains").then(({ data }) => setS({ v: data.domains.join(", "), auto: data.auto_join === true, ami: data.ami })); }, []);
  if (!s) return null;
  const save = async (next) => {
    try {
      const { data } = await api.put("/agency/domains", { domains: next.v.split(/[,\s]+/), auto_join: next.auto });
      setS({ ...next, v: data.domains.join(", ") }); toast.success("Entrada automática guardada");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const first = s.v.split(/[,\s]+/).filter(Boolean)[0];
  return (
    <div className={card} data-testid="domains-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg text-ec-green">Registo na agência («Registar-me»)</h3>
          <p className="text-sm text-slate-500 mt-1" data-testid="auto-join-text">{s.auto
            ? <>Ligado: quem se registar no ecrã de entrada com um email @{first || "domínio"} entra logo, como Consultor ou Gestor processual (escolhe no registo). Com outro email, só por convite.</>
            : <>Desligado: só se entra na agência por convite da Direção.</>}</p>
        </div>
        <Switch checked={s.auto} onCheckedChange={(auto) => save({ ...s, auto })} data-testid="auto-join-switch" />
      </div>
      <Label className="mt-4 block">Domínios de email da agência</Label>
      <div className="flex gap-2 mt-1.5">
        <Input value={s.v} onChange={(e) => setS({ ...s, v: e.target.value })} placeholder="ex.: kwportugal.pt" data-testid="domains-input" />
        <button onClick={() => save(s)} data-testid="domains-save" className="px-4 rounded-lg bg-ec-green text-white text-sm font-semibold hover:bg-ec-greenDark transition-colors">Guardar</button>
      </div>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "consultor" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/invites", form);
      toast.success(data.emailed ? `Convite enviado por email para ${data.email}.` : "Convite criado, mas o email não seguiu. Copie o link em «Convites pendentes».");
      setForm({ name: "", email: "", role: "consultor" }); onOpenChange(false); onDone();
    } catch (ex) { setErr(apiErr(ex.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green">Convidar para a equipa</DialogTitle>
          <DialogDescription>A pessoa recebe por email um link para criar a conta.</DialogDescription>
        </DialogHeader>
        {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nome e apelido</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="inv-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="inv-email-input" /></div>
          <div><Label>Papel</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="inv-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consultor">Consultor</SelectItem>
                <SelectItem value="gestor">Gestor processual</SelectItem>
                <SelectItem value="direccao">Direção</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-ec-green text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-ec-greenDark transition-colors" data-testid="create-invite">
            <UserPlus size={15} /> Criar convite
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, act, me }) {
  const setRole = (role) => act(() => api.put(`/users/${u.id}/role`, { role }), "Papel atualizado");
  const savePhone = (phone) => act(() => api.patch(`/users/${u.id}`, { phone }), "Telemóvel guardado");
  const del = () => act(() => api.delete(`/users/${u.id}`), "Utilizador removido");
  const off = () => act(() => api.post(`/users/${u.id}/deactivate`), `${u.name} desativado(a)`);
  return (
    <div className="py-3 flex flex-wrap items-center gap-3" data-testid={`user-${u.email}`}>
      {u.photo ? <img src={u.photo} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-sm font-semibold text-ec-green">{(u.name || "?").slice(0, 1)}</span>}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ec-green">{u.name}</div>
        <div className="text-xs text-slate-500 truncate">{u.email}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <MessageCircle size={12} className="text-slate-400" />
        <Input defaultValue={u.phone || ""} placeholder="Telemóvel" onBlur={(e) => e.target.value !== (u.phone || "") && savePhone(e.target.value)} className="h-8 w-36 text-xs" data-testid={`user-phone-${u.email}`} />
      </div>
      <Select value={u.role} onValueChange={setRole}>
        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="consultor">Consultor</SelectItem>
          <SelectItem value="gestor">Gestor processual</SelectItem>
          <SelectItem value="direccao">Direção</SelectItem>
        </SelectContent>
      </Select>
      {u.id !== me && <button onClick={off} className="text-xs text-slate-500 hover:text-amber-700 underline-offset-2 hover:underline" data-testid={`deactivate-user-${u.email}`}>Desativar</button>}
      <button onClick={del} disabled={u.role === "direccao"} className="text-slate-400 hover:text-red-600 transition-colors disabled:invisible" data-testid={`del-user-${u.email}`}><Trash2 size={16} /></button>
    </div>
  );
}

export function Equipa() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState(false);
  const load = () => {
    api.get("/users", { params: { all: true } }).then(({ data }) => setUsers(data));
    api.get("/invites").then(({ data }) => setInvites(data));
  };
  useEffect(load, []);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    load();
  };
  const pending = users.filter((u) => u.status === "pendente");
  const active = users.filter((u) => !["pendente", "recusado", "desativado"].includes(u.status));
  const off = users.filter((u) => u.status === "desativado");
  return (
    <div className="space-y-6 max-w-4xl">
      {pending.length > 0 && (
        <div className={card + " border-amber-200"} data-testid="pending-users">
          <h3 className="font-heading text-lg text-ec-green mb-3">Pedidos de acesso ({pending.length})</h3>
          {pending.map((u) => (
            <div key={u.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`pending-${u.email}`}>
              <div className="min-w-0"><div className="font-medium text-ec-green">{u.name}</div><div className="text-xs text-slate-500">{u.email} · pede {ROLE_LABEL[u.role]}</div></div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => act(() => api.post(`/users/${u.id}/approve`), `${u.name} aprovado(a)`)} data-testid={`approve-${u.email}`} className="text-xs px-3 py-1.5 rounded-lg bg-ec-green text-white font-semibold">Aprovar</button>
                <button onClick={() => act(() => api.post(`/users/${u.id}/reject`), "Pedido recusado")} data-testid={`reject-${u.email}`} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600">Recusar</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={card}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="font-heading text-lg text-ec-green">Equipa ({active.length})</h3>
          <button onClick={() => setInviting(true)} className="inline-flex items-center gap-2 bg-ec-green text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors" data-testid="invite-open-btn">
            <UserPlus size={15} /> Convidar
          </button>
        </div>
        <div className="divide-y divide-slate-100">{active.map((u) => <UserRow key={u.id} u={u} act={act} me={user.id} />)}</div>
        {invites.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="pending-invites">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Convites pendentes</div>
            {invites.map((i) => (
              <div key={i.token} className="flex items-center justify-between gap-2 text-sm py-1.5">
                <div className="min-w-0 truncate"><span className="font-medium text-ec-green">{i.name}</span> · {i.email} · {ROLE_LABEL[i.role]}</div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => { navigator.clipboard.writeText(i.url); toast.success("Link copiado"); }}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"><Copy size={13} /> Copiar link</button>
                  <button onClick={() => act(() => api.delete(`/invites/${i.token}`), "Convite cancelado")} aria-label="Cancelar convite"
                    className="text-slate-400 hover:text-red-600 px-1.5" data-testid={`invite-cancel-${i.email}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {off.length > 0 && (
        <div className={card} data-testid="deactivated-users">
          <h3 className="font-heading text-lg text-ec-green">Desativados ({off.length})</h3>
          <p className="text-sm text-slate-500 mb-2">Não conseguem entrar. Os processos e o histórico ficam guardados.</p>
          {off.map((u) => (
            <div key={u.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`deactivated-${u.email}`}>
              <div className="min-w-0"><div className="font-medium text-slate-500">{u.name}</div><div className="text-xs text-slate-400 truncate">{u.email} · {ROLE_LABEL[u.role]}</div></div>
              <button onClick={() => act(() => api.post(`/users/${u.id}/reactivate`), `${u.name} reativado(a)`)} data-testid={`reactivate-${u.email}`}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-ec-green font-semibold hover:bg-slate-50">Reativar</button>
            </div>
          ))}
        </div>
      )}
      <EntradaAutomatica />
      <InviteDialog open={inviting} onOpenChange={setInviting} onDone={load} />
    </div>
  );
}

function Documentos() {
  const [templates, setTemplates] = useState([]);
  const [edits, setEdits] = useState({});
  useEffect(() => { api.get("/config/templates").then(({ data }) => setTemplates(data)); }, []);
  const setEdit = (key, field, val, numeric = false) =>
    setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), key, [field]: numeric ? (val === "" ? null : Number(val)) : val } }));
  const value = (t, field) => (edits[t.key]?.[field] !== undefined ? edits[t.key][field] ?? "" : t[field] ?? "");
  const save = async () => {
    try {
      const { data } = await api.put("/config/templates", { edits: Object.values(edits) });
      setTemplates(data); setEdits({}); toast.success("Documentos guardados");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-ec-green">Lista de documentos e textos «Como pedir»</h3>
          <p className="text-sm text-slate-500">O nome e o texto são o que o cliente vê. Antecedência em dias antes da escritura; validade em meses. A plataforma define a validade máxima e a antecedência mínima: aqui só pode pôr uma validade mais curta ou uma antecedência maior.</p>
        </div>
        <button onClick={save} className="bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors shrink-0" data-testid="save-config">Guardar</button>
      </div>
      {templates.map((t) => (
        <div key={t.key} className={card} data-testid={`cfg-${t.key}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-ec-green">{t.name}</div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">{t.delivered_by} · {t.base === "cpcv" ? "CPCV" : "Escritura"}</span>
          </div>
          {t.condition === "optional" && (
            <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={value(t, "enabled") === true} onChange={(e) => setEdit(t.key, "enabled", e.target.checked)} data-testid={`cfg-enabled-${t.key}`} />
              Pedir este documento (opcional, desligado por omissão)
            </label>
          )}
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div><Label className="text-xs">Nome para o cliente</Label>
              <Input value={value(t, "client_name")} onChange={(e) => setEdit(t.key, "client_name", e.target.value)} className="mt-1" data-testid={`cfg-client-name-${t.key}`} /></div>
            <div className="flex gap-3">
              <div className="flex-1"><Label className="text-xs">Antecedência (dias)</Label>
                <Input type="number" disabled={t.base === "cpcv"} value={value(t, "offset_days")} onChange={(e) => setEdit(t.key, "offset_days", e.target.value, true)} className="mt-1" data-testid={`cfg-offset-${t.key}`} />
                {t.base !== "cpcv" && t.limit_offset_days != null && <div className="text-[11px] text-slate-400 mt-1" data-testid={`cfg-offset-limit-${t.key}`}>No máximo {t.limit_offset_days}</div>}</div>
              <div className="flex-1"><Label className="text-xs">Validade (meses)</Label>
                <Input type="number" value={value(t, "validity_months")} onChange={(e) => setEdit(t.key, "validity_months", e.target.value, true)} placeholder="—" className="mt-1" data-testid={`cfg-validity-${t.key}`} />
                {t.limit_validity_months != null && <div className="text-[11px] text-slate-400 mt-1" data-testid={`cfg-validity-limit-${t.key}`}>No máximo {t.limit_validity_months} meses</div>}</div>
            </div>
          </div>
          <div className="mt-3"><Label className="text-xs">Texto «Como pedir»</Label>
            <Textarea value={value(t, "help")} onChange={(e) => setEdit(t.key, "help", e.target.value)} rows={5} className="mt-1 text-sm" placeholder="Para que serve… Como obter: 1. … 2. …" data-testid={`cfg-help-${t.key}`} /></div>
        </div>
      ))}
    </div>
  );
}

function Demonstracao() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); } };
  const showcase = (complete) => run(complete ? "complete" : "showcase", async () => {
    const { data } = await api.post("/demo/seed-showcase", null, { params: complete ? { complete: true } : {} });
    toast.success(`Processo ${data.created} criado`); navigate(`/processo/${data.id}`);
  });
  const simple = () => run("simple", async () => { await api.post("/demo/seed-examples"); toast.success("3 processos de exemplo criados"); });
  const clear = () => run("clear", async () => { const { data } = await api.post("/demo/clear-examples"); toast.success(`${data.removed} processo(s) de exemplo apagado(s)`); });
  const btn = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50";
  return (
    <div className={card + " space-y-5"} data-testid="demo-panel">
      <div>
        <div className="font-heading text-xl text-ec-green">Processo de demonstração</div>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Só na agência de demonstração. Cria vendas fictícias com documentos já lidos, avisos e Portal do Cliente ativo.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={() => showcase(false)} disabled={!!busy} data-testid="demo-showcase-btn" className={`${btn} bg-ec-gold text-ec-green`}><Sparkles size={16} /> {busy === "showcase" ? "A criar…" : "Criar processo de demonstração"}</button>
        <button onClick={() => showcase(true)} disabled={!!busy} data-testid="demo-complete-btn" className={`${btn} bg-ec-green text-white`}><CheckCircle2 size={16} /> {busy === "complete" ? "A criar…" : "Criar processo concluído"}</button>
        <button onClick={simple} disabled={!!busy} data-testid="demo-simple-btn" className={`${btn} border border-slate-200 text-slate-700`}>{busy === "simple" ? "A criar…" : "Criar 3 exemplos simples"}</button>
        <button onClick={clear} disabled={!!busy} data-testid="demo-clear-btn" className={`${btn} border border-slate-200 text-slate-600`}><Trash2 size={15} /> {busy === "clear" ? "A apagar…" : "Apagar exemplos"}</button>
      </div>
    </div>
  );
}

function ClearExamples() {
  const run = async () => {
    try { const { data } = await api.post("/demo/clear-examples"); toast.success(`${data.removed} processo(s) de exemplo apagado(s)`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card + " flex flex-col sm:flex-row sm:items-center justify-between gap-4"} data-testid="clear-examples-card">
      <div>
        <h3 className="font-heading text-lg text-ec-green">Processos de exemplo</h3>
        <p className="text-sm text-slate-500">Apaga os processos de exemplo criados para demonstração. Os números da equipa fictícia mantêm-se.</p>
      </div>
      <button onClick={run} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 shrink-0" data-testid="clear-examples-settings-btn"><Trash2 size={15} /> Apagar processos de exemplo</button>
    </div>
  );
}

function WhatsAppStatus() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/agency/twilio-wa").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const on = (s.available && s.enabled) || s.templates_ready > 0;
  return (
    <div className={card} data-testid="whatsapp-status-card">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-heading text-lg text-ec-green">WhatsApp automático</h3>
        <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="whatsapp-status">{on ? "Ativo" : "Desligado"}</span>
      </div>
      <p className="text-sm text-slate-500 mt-1">Gerido pela plataforma ImoDesk. {on ? "As mensagens automáticas saem pelo número de WhatsApp do ImoDesk." : "Os lembretes abrem o WhatsApp do consultor para enviar."}</p>
    </div>
  );
}

export default function Definicoes() {
  const { user } = useAuth();
  if (user?.role !== "direccao") return <Navigate to="/inicio" replace />;
  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-ec-green">Definições da agência</h1>
        <p className="text-slate-500 mt-1">Dados da agência, equipa, marca, documentos e mensagens.</p>
      </div>
      <SetupCard />
      <Tabs defaultValue="agencia">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="agencia" data-testid="tab-agencia">Agência</TabsTrigger>
          <TabsTrigger value="equipa" data-testid="tab-equipa">Equipa</TabsTrigger>
          <TabsTrigger value="gestao" data-testid="tab-gestao">Gestão processual</TabsTrigger>
          <TabsTrigger value="marca" data-testid="tab-marca">Marca</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos e mensagens</TabsTrigger>
          {user.agency_is_demo && user.real_platform_admin && <TabsTrigger value="demo" data-testid="tab-demo">Demonstração</TabsTrigger>}
        </TabsList>
        <TabsContent value="agencia"><div className="space-y-6"><AgenciaDefinicoes /><ReviewBy /><AutoAccept /><TreatDefault /><CmiDefaults /><CmiCertToggle /><AssinajaToggle /><WhatsAppStatus /><KycToggle />{user.agency_is_demo && user.real_platform_admin && <ClearExamples />}</div></TabsContent>
        <TabsContent value="equipa"><Equipa /></TabsContent>
        <TabsContent value="gestao"><GestaoDefinicoes /></TabsContent>
        <TabsContent value="marca"><Marca /></TabsContent>
        <TabsContent value="documentos"><div className="space-y-8"><MensagensDefinicoes /><Documentos /></div></TabsContent>
        {user.agency_is_demo && user.real_platform_admin && <TabsContent value="demo"><div className="space-y-6"><DemoBranding /><Demonstracao /></div></TabsContent>}
      </Tabs>
    </div>
  );
}
